import React, { useState } from "react";

const INDIA_MAP_DEFAULT = "/assets/General/india.png";

const AdditionalInfo = ({
  weight,
  beesUsed,
  harvestLocation,
  segmentation,
  logos,
  shelfLife,
  storage,
  mapImage,
  mapX,
  mapY,
}) => {
  const netQty = weight ? `${weight * 1000} g` : null;
  const shelfLifeText = shelfLife || "24 months";

  // ---- Build cards list ----
  const qtyShelfCard =
    netQty || shelfLifeText
      ? {
          type: "qtyShelf",
          qty: netQty,
          shelfLife: shelfLifeText,
        }
      : null;

  const cards = [
    qtyShelfCard,
    storage && {
      type: "simple",
      label: "Storage",
      value:
        storage || "Store in a cool, dry place away from sunlight.",
    },
    beesUsed && { type: "simple", label: "Bees Used", value: beesUsed },
    segmentation && {
      type: "simple",
      label: "Segmentation",
      value: segmentation,
    },
  ].filter(Boolean);

  const finalMapImage = mapImage || INDIA_MAP_DEFAULT;
  const hasMap = !!finalMapImage;
  const hasLogos = Array.isArray(logos) && logos.length > 0;

  // Hooks must be before any early return
  const [infoOpen, setInfoOpen] = useState(cards.length > 0);
  const [mapOpen, setMapOpen] = useState(false);
  const [certOpen, setCertOpen] = useState(hasLogos);

  // If nothing at all, don't render
  if (!cards.length && !hasMap && !hasLogos) return null;

  // Marker in percentage (0–100); fallback to center if not provided
  const markerLeft =
    typeof mapX === "number" && !Number.isNaN(mapX) ? mapX : 50;
  const markerTop =
    typeof mapY === "number" && !Number.isNaN(mapY) ? mapY : 50;

  const Chevron = ({ open }) => (
    <span
      className={[
        "ml-3 inline-flex h-7 w-7 items-center justify-center rounded-full bg-amber-100 text-amber-700 transition-transform duration-200",
        open ? "rotate-90" : "",
      ].join(" ")}
    >
      <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true">
        <path
          fill="currentColor"
          d="M9 18l6-6l-6-6"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  );

  return (
    <section className="mt-10 animate-slide-in">
      <h2 className="text-lg sm:text-xl font-bold mb-3 text-gray-900">
        Additional Information
      </h2>

      <div className="space-y-3 sm:space-y-4">
        {/* ========== INFO DROPDOWN ========== */}
        {cards.length > 0 && (
          <div className="rounded-2xl border border-amber-100 bg-gradient-to-r from-amber-50 via-white to-amber-50 shadow-sm">
            <button
              type="button"
              onClick={() => setInfoOpen((o) => !o)}
              className="w-full flex items-center justify-between px-4 py-3 sm:px-5 sm:py-4 text-left"
            >
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-amber-600">
                  Quantity, shelf life, storage & origin notes
                </div>
                {/* <div className="text-sm sm:text-base text-gray-800">
                  Quantity, shelf life, storage & origin notes
                </div> */}
              </div>
              <Chevron open={infoOpen} />
            </button>

            <div
              className={[
                "transition-all duration-300 ease-out overflow-hidden",
                infoOpen ? "max-h-[900px] opacity-100" : "max-h-0 opacity-0",
              ].join(" ")}
            >
              <div className="px-4 pb-4 sm:px-5 sm:pb-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  {cards.map((item, idx) => {
                    // Special card: Quantity + Shelf Life in one box
                    if (item.type === "qtyShelf") {
                      return (
                        <div
                          key={"qtyShelf-" + idx}
                          className="group relative overflow-hidden rounded-2xl bg-white/95 border border-gray-100 px-4 py-3 sm:px-5 sm:py-4 shadow-[0_10px_25px_rgba(15,23,42,0.04)] transition-transform duration-200 hover:-translate-y-0.5 hover:shadow-[0_14px_30px_rgba(15,23,42,0.08)]"
                        >
                          <div className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-gradient-to-tr from-amber-50/40 via-transparent to-amber-100/50" />
                          <div className="relative">
                            <div className="flex flex-wrap gap-x-6 gap-y-3">
                              {item.qty && (
                                <div className="min-w-[110px]">
                                  <div className="text-[11px] uppercase tracking-wide font-semibold text-amber-500">
                                    Quantity
                                  </div>
                                  <div className="mt-1.5 text-sm sm:text-base font-semibold text-gray-900 leading-snug">
                                    {item.qty}
                                  </div>
                                </div>
                              )}
                              {item.shelfLife && (
                                <div className="min-w-[110px]">
                                  <div className="text-[11px] uppercase tracking-wide font-semibold text-amber-500">
                                    Shelf Life
                                  </div>
                                  <div className="mt-1.5 text-sm sm:text-base font-semibold text-gray-900 leading-snug">
                                    {item.shelfLife}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    }

                    // Normal single-value cards
                    return (
                      <div
                        key={item.label + idx}
                        className="group relative overflow-hidden rounded-2xl bg-white/95 border border-gray-100 px-4 py-3 sm:px-5 sm:py-4 shadow-[0_10px_25px_rgba(15,23,42,0.04)] transition-transform duration-200 hover:-translate-y-0.5 hover:shadow-[0_14px_30px_rgba(15,23,42,0.08)]"
                      >
                        <div className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-gradient-to-tr from-amber-50/40 via-transparent to-amber-100/50" />
                        <div className="relative">
                          <div className="text-[11px] uppercase tracking-wide font-semibold text-amber-500">
                            {item.label}
                          </div>
                          <div className="mt-1.5 text-sm sm:text-base font-semibold text-gray-900 leading-snug">
                            {item.value}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ========== MAP DROPDOWN ========== */}
        {hasMap && (
          <div className="rounded-2xl border border-amber-100 bg-gradient-to-r from-white via-amber-50 to-white shadow-sm">
            <button
              type="button"
              onClick={() => setMapOpen((o) => !o)}
              className="w-full flex items-center justify-between px-4 py-3 sm:px-5 sm:py-4 text-left"
            >
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-amber-600">
                  Harvest Map
                </div>
                <div className="text-sm sm:text-base text-gray-800">
                  Visual location of the apiary in India
                </div>
              </div>
              <Chevron open={mapOpen} />
            </button>

            <div
              className={[
                "transition-all duration-300 ease-out overflow-hidden",
                mapOpen ? "max-h-[700px] opacity-100" : "max-h-0 opacity-0",
              ].join(" ")}
            >
              <div className="px-4 pb-4 sm:px-5 sm:pb-5">
                <div className="relative rounded-2xl bg-white/95 border border-gray-100 shadow-[0_10px_25px_rgba(15,23,42,0.04)] overflow-hidden flex flex-col">
                  <div className="px-4 pt-3 sm:px-5 sm:pt-4 flex items-center justify-between">
                    <div>
                      <div className="text-xs font-medium text-slate-500">
                        Harvest Region
                      </div>
                      {harvestLocation && (
                        <div className="text-sm font-semibold text-gray-900">
                          {harvestLocation}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="mt-2 relative flex-1">
                    <div className="relative w-full h-56 sm:h-64">
                      <img
                        src={finalMapImage}
                        alt="India map showing harvest region"
                        className="h-full w-full object-contain"
                        loading="lazy"
                      />

                      {/* Marker */}
                      <div
                        className="absolute flex flex-col items-center transition-transform duration-200"
                        style={{
                          left: `${markerLeft}%`,
                          top: `${markerTop}%`,
                          transform: "translate(-50%, -100%)",
                        }}
                      >
                        <span className="h-3.5 w-3.5 rounded-full bg-amber-600 border-2 border-white shadow-md" />
                        {harvestLocation && (
                          <span className="mt-1 rounded-full bg-white/95 px-2 py-1 text-[10px] sm:text-[11px] font-medium text-gray-800 shadow">
                            {harvestLocation}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <p className="px-4 pb-3 sm:px-5 sm:pb-4 text-[11px] text-gray-500">
                    Marker is approximate and based on the declared harvest
                    area.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ========== CERTIFICATIONS DROPDOWN ========== */}
        {hasLogos && (
          <div className="rounded-2xl border border-amber-100 bg-gradient-to-r from-amber-50 via-white to-amber-50 shadow-sm">
            <button
              type="button"
              onClick={() => setCertOpen((o) => !o)}
              className="w-full flex items-center justify-between px-4 py-3 sm:px-5 sm:py-4 text-left"
            >
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-amber-600">
                  Verified quality, safety and processing seals
                </div>
                {/* <div className="text-sm sm:text-base text-gray-800">
                  Verified quality, safety and processing seals
                </div> */}
              </div>
              <Chevron open={certOpen} />
            </button>

            <div
              className={[
                "transition-all duration-300 ease-out overflow-hidden",
                certOpen ? "max-h-[400px] opacity-100" : "max-h-0 opacity-0",
              ].join(" ")}
            >
              <div className="px-4 pb-4 sm:px-5 sm:pb-5">
                <div className="flex flex-wrap items-center gap-4 sm:gap-6">
                  {logos.map((src, idx) => (
                    <img
                      key={idx}
                      src={src}
                      alt="certification"
                      className="h-10 sm:h-14 w-auto object-contain opacity-90 hover:opacity-100 transition"
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default AdditionalInfo;
